/**
 * @file ProfilePage.jsx
 * @description The main user profile page where users can view and edit their
 *              personal, academic, and social details. Displays earned badges,
 *              profile completion progress, and statistics.
 *
 * @module components/ProfilePage
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPut } from '../utils/apiUtils';
import {
    FiUser,
    FiEdit3,
    FiSave,
    FiX,
    FiMail,
    FiCalendar,
    FiMapPin,
    FiGithub,
    FiLinkedin,
    FiYoutube,
    FiInstagram,
    FiMenu,
    FiLogOut,
    FiHome,
    FiCode,
    FiLayers,
    FiTarget,
    FiAward,
    FiFilter,
    FiRefreshCw,
    FiAlertCircle,
    FiCheck,
    FiSidebar,
    FiChevronRight,
    FiLock,
    FiUnlock,
    FiBookOpen,
    FiBarChart2,
    FiTrendingUp,
    FiActivity,
    FiSettings,
    FiGlobe,
    FiChevronDown,
    FiChevronUp,
    FiEye,
    FiEyeOff
} from 'react-icons/fi';
import { FaFire, FaTrophy, FaGraduationCap, FaStar, FaIdCard } from 'react-icons/fa';
import BadgeImage from './BadgeImage';
import UserAvatar from './UserAvatar';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';

/* ══════════════════════════════════════════════════════════════
 * SUB-COMPONENTS & UTILITIES
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component CustomSelect
 * @description A custom styled dropdown component that replaces default HTML selects.
 *              Automatically positions dropdown above or below based on screen space.
 * 
 * @param {Object} props
 * @param {string} props.value - Currently selected value.
 * @param {Function} props.onChange - Callback fired when an option is selected.
 * @param {Array<{value: string, label: string}>} props.options - Available select options.
 * @param {string} props.placeholder - Text shown when no option is selected.
 * @param {boolean} [props.disabled=false] - Whether the select is disabled.
 */
const CustomSelect = ({ value, onChange, options, placeholder, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(
        options.find(opt => opt.value === value) || null
    );
    const [openUp, setOpenUp] = useState(false);
    const buttonRef = useRef(null);

    useEffect(() => {
        setSelectedOption(options.find(opt => opt.value === value) || null);
    }, [value, options]);

    const toggleDropdown = () => {
        if (disabled) return;

        if (!isOpen) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow < 200 && spaceAbove > spaceBelow) {
                setOpenUp(true);
            } else {
                setOpenUp(false);
            }
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (option) => {
        setSelectedOption(option);
        onChange(option.value);
        setIsOpen(false);
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                onClick={toggleDropdown}
                disabled={disabled}
                className={`w-full bg-accent text-foreground px-4 py-3 rounded-t border-b-2 border-transparent
                focus:outline-none focus:border-emerald-600 transition-colors duration-200 flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-emerald-50'
                    }`}
            >
                <span className={selectedOption && selectedOption.value !== '' ? 'text-foreground' : 'text-gray-400'}>
                    {selectedOption && selectedOption.value !== '' ? selectedOption.label : placeholder}
                </span>
                {!disabled && (
                    <div className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                        <FiChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                )}
            </button>

            {isOpen && !disabled && (
                <div
                    className={`absolute left-0 right-0 mt-1 bg-white border border-border shadow-xl rounded z-50 max-h-60 overflow-y-auto
                        ${openUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                >
                    {options.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => handleSelect(option)}
                            className="w-full text-left bg-white border-b border-gray-100 text-foreground px-4 py-3 
                            focus:outline-none focus:bg-emerald-50 transition-colors duration-200 hover:bg-emerald-50"
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * @component CustomDatePicker
 * @description A custom styled date picker input enforcing the YYYY-MM-DD format dynamically.
 *              Automatically inserts hyphens as the user types.
 * 
 * @param {Object} props
 * @param {string} props.value - The current date string.
 * @param {Function} props.onChange - Callback fired on date change.
 * @param {boolean} [props.disabled=false] - Whether the input is disabled.
 * @param {string} [props.placeholder="Select date"] - Input placeholder text.
 */
const CustomDatePicker = ({ value, onChange, disabled = false, placeholder = "Select date" }) => {
    return (
        <input
            type="text"
            value={value || ''}
            onChange={(e) => {
                const input = e.target.value;
                const digits = input.replace(/\D/g, '');
                let formatted = digits;
                if (digits.length > 4) {
                    formatted = digits.slice(0, 4) + '-' + digits.slice(4);
                }
                if (digits.length > 6) {
                    formatted = formatted.slice(0, 7) + '-' + digits.slice(6, 8);
                }
                onChange(formatted);
            }}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full bg-accent text-foreground px-4 py-3 rounded-t border-b-2 border-transparent
                focus:outline-none focus:border-emerald-600 transition-colors duration-200
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-emerald-50'}`}
        />
    );
};

/**
 * @component ProfileCompletionProgress
 * @description Renders a progress bar indicating how complete the user's profile is.
 *              Colors dynamically shift from red -> yellow -> blue -> green as it nears 100%.
 * 
 * @param {Object} props
 * @param {number} props.completionPercentage - An integer between 0 and 100.
 */
const ProfileCompletionProgress = ({ completionPercentage }) => {
    const getProgressColor = () => {
        if (completionPercentage >= 100) return 'from-emerald-500 to-green-400';
        if (completionPercentage >= 75) return 'from-blue-500 to-cyan-400';
        if (completionPercentage >= 50) return 'from-yellow-500 to-orange-400';
        return 'from-red-500 to-pink-400';
    };

    return (
        <div className="mb-2">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-foreground">Profile Completion</h3>
                </div>
                <div className="flex items-center space-x-2">
                    <span className="text-lg font-bold text-foreground">{completionPercentage}%</span>
                    {completionPercentage >= 100 && <FaStar className="text-yellow-400 w-5 h-5" />}
                </div>
            </div>

            <div className="w-full bg-gray-300 rounded-full h-1 mb-3 overflow-hidden">
                <div
                    className={`h-1 rounded-full bg-gradient-to-r ${getProgressColor()}`}
                    style={{ width: `${completionPercentage}%`, transition: 'width 1s ease-out' }}
                />
            </div>

            {completionPercentage < 100 && (
                <p className="text-xs text-muted-foreground mt-1 text-left">
                    Complete all sections to earn the Storyteller badge!
                </p>
            )}
        </div>
    );
};

/**
 * Helper to get the correct icon based on badge type.
 * @param {string} badgeName - Internal identifier for the badge type.
 * @returns {React.ComponentType} React Icon component.
 */
const getBadgeIcon = (badgeName) => {
    switch (badgeName) {
        case 'streak_master': return FaFire;
        case 'problem_crusher': return FiTarget;
        case 'daily_champion': return FaTrophy;
        case 'storyteller': return FiBookOpen;
        default: return FiAward;
    }
};

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component ProfilePage
 * @description The main profile view for the authenticated user.
 *              Manages profile data fetching, editing, and badge/achievement display.
 * 
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 */
const ProfilePage = ({ navigate }) => {
    const { isAuthenticated, user, logout } = useAuth();

    // Navigation states
    const [selectedCategory, setSelectedCategory] = useState('personal');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileCategoriesOpen, setIsMobileCategoriesOpen] = useState(false);

    // Profile data states
    const [profileData, setProfileData] = useState(null);
    const [badges, setBadges] = useState([]);
    const [badgeProgress, setBadgeProgress] = useState(null);
    const [userStats, setUserStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [completionPercentage, setCompletionPercentage] = useState(0);

    // Edit mode states
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Badge filter state
    const [badgeFilter, setBadgeFilter] = useState('all');

    const [showBadgePopup, setShowBadgePopup] = useState(false);
    const [earnedBadges, setEarnedBadges] = useState([]);

    // Add these to your existing states
    const [changePasswordData, setChangePasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [newPasswordFocused, setNewPasswordFocused] = useState(false);
    const [newPasswordValidation, setNewPasswordValidation] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        symbol: false
    });

    // Add this function before your render methods
    const showImmediateBadgePopup = (badges) => {
        setEarnedBadges(badges);
        setShowBadgePopup(true);
    };

    const closeBadgePopup = () => {
        setShowBadgePopup(false);
        setEarnedBadges([]);
    };

    // Categories configuration
    const categories = [
        { id: 'personal', name: 'Personal Details', icon: FaIdCard, description: 'Basic personal information', editable: true },
        { id: 'academic', name: 'Academic Details', icon: FaGraduationCap, description: 'Educational background', editable: true },
        { id: 'social', name: 'Social Links', icon: FiGlobe, description: 'Social media profiles', editable: true },
        { id: 'achievements', name: 'Achievements', icon: FiAward, description: 'Badges and milestones', editable: false }
    ];

    // Standardized links for Navbar & Footer
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard") },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ];

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "Dashboard", action: () => navigate("/dashboard") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    // Options
    const genderOptions = [
        { value: '', label: 'Select gender' },
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say' }
    ];

    const academicLevelOptions = [
        { value: '', label: 'Select academic level' },
        { value: 'high_school', label: 'High School' },
        { value: 'undergraduate', label: 'Undergraduate' },
        { value: 'graduate', label: 'Graduate' },
        { value: 'postgraduate', label: 'Postgraduate' },
        { value: 'phd', label: 'PhD' }
    ];

    // Validate new password in real-time (add this function)
    const validateNewPassword = (pwd) => {
        const validation = {
            length: pwd.length >= 6,
            uppercase: /[A-Z]/.test(pwd),
            lowercase: /[a-z]/.test(pwd),
            number: /\d/.test(pwd),
            symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)
        };
        setNewPasswordValidation(validation);
        return validation;
    };

    // Check if new password meets all requirements (add this function)
    const isNewPasswordValid = () => {
        const { length, uppercase, lowercase, number, symbol } = newPasswordValidation;
        return length && uppercase && lowercase && number && symbol;
    };

    // Mobile menu handlers
    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
    const closeMobileMenu = () => setIsMobileMenuOpen(false);
    const toggleMobileCategories = () => setIsMobileCategoriesOpen(!isMobileCategoriesOpen);
    const closeMobileCategories = () => setIsMobileCategoriesOpen(false);

    const handleNavClick = (path) => {
        navigate(path);
        closeMobileMenu();
    };

    const handleCategorySelect = (categoryId) => {
        setSelectedCategory(categoryId);
        setIsEditing(false);
        setSaveError(null);
        if (window.innerWidth < 1024) {
            closeMobileCategories();
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleChangePassword = async () => {
        try {
            setSaving(true);
            setSaveError(null);

            if (!changePasswordData.currentPassword) {
                setSaveError('Current password is required');
                setTimeout(() => setSaveError(null), 5000);
                return;
            }

            if (!changePasswordData.newPassword) {
                setSaveError('New password is required');
                setTimeout(() => setSaveError(null), 5000);
                return;
            }

            if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
                setSaveError('New passwords do not match');
                setTimeout(() => setSaveError(null), 5000);
                return;
            }

            if (!isNewPasswordValid()) {
                setSaveError('New password does not meet requirements');
                setTimeout(() => setSaveError(null), 5000);
                return;
            }

            const response = await apiPut(`/auth/change-password/${user.id}`, {
                currentPassword: changePasswordData.currentPassword,
                newPassword: changePasswordData.newPassword
            });

            if (response.ok) {
                setChangePasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setNewPasswordValidation({ length: false, uppercase: false, lowercase: false, number: false, symbol: false });
                setIsEditing(false);
                setSaveError('Password updated successfully!');
                setTimeout(() => setSaveError(null), 3000);
            } else {
                const errorData = await response.json();
                setSaveError(errorData.error || 'Failed to change password');
                setTimeout(() => setSaveError(null), 5000);
            }
        } catch (err) {
            setSaveError('Failed to change password');
            setTimeout(() => setSaveError(null), 5000);
        } finally {
            setSaving(false);
        }
    };

    const calculateCompletionPercentage = (profile) => {
        if (!profile) return 0;
        const requiredFields = [
            'name', 'date_of_birth', 'gender', 'degree', 'institute',
            'institute_email', 'graduation_year', 'field_of_study', 'academic_level',
            'github_link', 'linkedin_link'
        ];
        const completedFields = requiredFields.filter(field => {
            const value = profile[field];
            return value !== null && value !== undefined && value.toString().trim() !== '';
        }).length;
        return Math.round((completedFields / requiredFields.length) * 100);
    };

    const fetchProfileData = async () => {
        if (!isAuthenticated || !user?.id) {
            setError('Authentication required');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const [profileRes, badgesRes, progressRes, statsRes] = await Promise.all([
                apiGet(`/auth/user-profile/${user.id}`),
                apiGet(`/badges/user/${user.id}`),
                apiGet(`/badges/progress/${user.id}`),
                apiGet(`/auth/stats/${user.id}`)
            ]);
            if (profileRes.ok) {
                const profile = await profileRes.json();
                setProfileData(profile);
                setEditData(profile);
                setCompletionPercentage(calculateCompletionPercentage(profile));
            }
            if (badgesRes.ok) {
                const badgeData = await badgesRes.json();
                setBadges(badgeData.badges || []);
            }
            if (progressRes.ok) {
                const progressData = await progressRes.json();
                setBadgeProgress(progressData);
            }
            if (statsRes.ok) {
                const stats = await statsRes.json();
                setUserStats(stats);
            }
        } catch (err) {
            setError('Failed to load profile data');
        } finally {
            setLoading(false);
        }
    };

    const saveProfile = async () => {
        try {
            setSaving(true);
            setSaveError(null);
            const response = await apiPut(`/auth/user-profile/${user.id}`, editData);
            if (response.ok) {
                const result = await response.json();
                setProfileData(result.profile);
                setEditData(result.profile);
                setCompletionPercentage(result.completionPercentage || calculateCompletionPercentage(result.profile));
                setIsEditing(false);
                if (result.newBadges && result.newBadges.length > 0) {
                    showImmediateBadgePopup(result.newBadges);
                    fetchProfileData();
                }
            } else {
                const errorData = await response.json();
                setSaveError(errorData.error || 'Failed to save profile');
            }
        } catch (err) {
            setSaveError('Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditData(profileData);
        setIsEditing(false);
        setSaveError(null);
    };

    const handleInputChange = (field, value) => {
        setEditData(prev => ({ ...prev, [field]: value }));
    };

    const getTierColor = (tier) => {
        switch (tier?.toLowerCase()) {
            case 'bronze': return { bg: 'from-amber-600 to-amber-700', border: 'border-amber-500', text: 'text-amber-400', glow: 'shadow-amber-500/20' };
            case 'silver': return { bg: 'from-slate-400 to-slate-500', border: 'border-slate-400', text: 'text-slate-300', glow: 'shadow-slate-500/20' };
            case 'gold': return { bg: 'from-yellow-500 to-yellow-600', border: 'border-yellow-400', text: 'text-yellow-300', glow: 'shadow-yellow-500/20' };
            case 'platinum': return { bg: 'from-purple-500 to-purple-600', border: 'border-purple-400', text: 'text-purple-300', glow: 'shadow-purple-500/20' };
            default: return { bg: 'from-gray-600 to-gray-700', border: 'border-gray-500', text: 'text-gray-400', glow: 'shadow-gray-500/20' };
        }
    };

    const getAllBadges = () => {
        if (!badgeProgress) return [];
        const allBadges = [];
        Object.keys(badgeProgress.progress).forEach(badgeType => {
            badgeProgress.progress[badgeType].forEach(badge => {
                allBadges.push({ ...badge, type: badgeType, earned: badge.earned });
            });
        });
        return allBadges.sort((a, b) => {
            const tierOrder = { bronze: 1, silver: 2, gold: 3, platinum: 4 };
            if (a.type !== b.type) return a.type.localeCompare(b.type);
            return tierOrder[a.badge_tier] - tierOrder[b.badge_tier];
        });
    };

    const getFilteredBadges = () => {
        const allBadges = getAllBadges();
        switch (badgeFilter) {
            case 'earned': return allBadges.filter(b => b.earned);
            case 'locked': return allBadges.filter(b => !b.earned);
            default: return allBadges;
        }
    };

    const renderCategoryContent = () => {
        const currentCategory = categories.find(cat => cat.id === selectedCategory);
        const isEditable = currentCategory?.editable;

        switch (selectedCategory) {
            case 'personal':
                return (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                            <div>
                                <h3 className="text-xl font-semibold text-foreground flex items-center space-x-2"><FaIdCard className="text-emerald-600 w-5 h-5" /><span>Personal Details</span></h3>
                            </div>
                            {isEditable && (
                                <div className="flex items-center space-x-3">
                                    {isEditing ? (
                                        <>
                                            <button onClick={saveProfile} disabled={saving} className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 rounded-lg font-medium transition-colors duration-200">{saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}<span className='hidden md:flex'>{saving ? 'Saving...' : 'Save'}</span></button>
                                            <button onClick={cancelEdit} disabled={saving} className="flex items-center space-x-0.5 text-muted-foreground hover:text-foreground rounded-lg font-medium transition-colors duration-200"><FiX className="w-4 h-4" /><span className='hidden md:flex'>Cancel</span></button>
                                        </>
                                    ) : (
                                        <button onClick={() => setIsEditing(true)} className="flex items-center space-x-2 py-2 text-emerald-600 hover:text-emerald-700 rounded-lg font-medium transition-colors duration-200"><FiEdit3 className="w-4 h-4" /><span className='hidden md:flex'>Edit</span></button>
                                    )}
                                </div>
                            )}
                        </div>
                        {saveError && <div className="bg-red-600 text-white rounded px-4 py-2">{saveError}</div>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2 flex items-center space-x-1"><span>Name</span><FiLock className="w-3 h-3" /></label>
                                <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.name || user?.name}</div>
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2 flex items-center space-x-1"><span>Username</span><FiLock className="w-3 h-3" /></label>
                                <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.username || user?.username}</div>
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2 flex items-center space-x-1"><span>Email</span><FiLock className="w-3 h-3" /></label>
                                <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.email || user?.email}</div>
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Date of Birth</label>
                                {isEditing ? <CustomDatePicker value={editData.date_of_birth || ''} onChange={(value) => handleInputChange('date_of_birth', value)} placeholder="YYYY-MM-DD" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.date_of_birth ? new Date(profileData.date_of_birth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}</div>}
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Gender</label>
                                {isEditing ? <CustomSelect value={editData.gender || ''} onChange={(value) => handleInputChange('gender', value)} options={genderOptions} placeholder="Select gender" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed capitalize placeholder:text-gray-400">{profileData?.gender?.replace('_', ' ') || '-'}</div>}
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Member Since</label>
                                <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Unknown'}</div>
                            </div>
                        </div>
                    </div>
                );
            case 'academic':
                return (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                            <div><h3 className="text-xl font-semibold text-foreground flex items-center space-x-2"><FaGraduationCap className="text-emerald-600 w-5 h-5" /><span>Academic Details</span></h3></div>
                            {isEditable && (
                                <div className="flex items-center space-x-3">
                                    {isEditing ? (
                                        <>
                                            <button onClick={saveProfile} disabled={saving} className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 rounded-lg font-medium transition-colors duration-200">{saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}<span className='hidden md:flex'>{saving ? 'Saving...' : 'Save'}</span></button>
                                            <button onClick={cancelEdit} disabled={saving} className="flex items-center space-x-0.5 text-muted-foreground hover:text-foreground rounded-lg font-medium transition-colors duration-200"><FiX className="w-4 h-4" /><span className='hidden md:flex'>Cancel</span></button>
                                        </>
                                    ) : (
                                        <button onClick={() => setIsEditing(true)} className="flex items-center space-x-2 py-2 text-emerald-600 hover:text-emerald-700 rounded-lg font-medium transition-colors duration-200"><FiEdit3 className="w-4 h-4" /><span className='hidden md:flex'>Edit</span></button>
                                    )}
                                </div>
                            )}
                        </div>
                        {saveError && <div className="bg-red-100 text-red-700 border-l-4 border-red-500 px-4 py-2">{saveError}</div>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Degree/Qualification</label>
                                {isEditing ? <input type="text" value={editData.degree || ''} onChange={(e) => handleInputChange('degree', e.target.value)} className="w-full bg-accent rounded-t text-foreground px-4 py-3 border-b-2 border-transparent focus:border-emerald-600 focus:outline-none transition-colors duration-200 placeholder-gray-400" placeholder="e.g., B.Tech Computer Science" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.degree || '-'}</div>}
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Institute/University</label>
                                {isEditing ? <input type="text" value={editData.institute || ''} onChange={(e) => handleInputChange('institute', e.target.value)} className="w-full bg-accent rounded-t text-foreground px-4 py-3 border-b-2 border-transparent focus:border-emerald-600 focus:outline-none transition-colors duration-200 placeholder-gray-400" placeholder="e.g., ABC University" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.institute || '-'}</div>}
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Institute/University E-mail</label>
                                {isEditing ? <input type="email" value={editData.institute_email || ''} onChange={(e) => handleInputChange('institute_email', e.target.value)} className="w-full bg-accent rounded-t text-foreground px-4 py-3 border-b-2 border-transparent focus:border-emerald-600 focus:outline-none transition-colors duration-200 placeholder-gray-400" placeholder="e.g., you@university.edu" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.institute_email || '-'}</div>}
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Institute Roll No</label>
                                {isEditing ? <input type="text" value={editData.institute_roll_no || ''} onChange={(e) => handleInputChange('institute_roll_no', e.target.value)} className="w-full bg-accent rounded-t text-foreground px-4 py-3 border-b-2 border-transparent focus:border-emerald-600 focus:outline-none transition-colors duration-200 placeholder-gray-400" placeholder="e.g., 21BCE1001" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.institute_roll_no || '-'}</div>}
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Graduation Year</label>
                                {isEditing ? <input type="text" max={new Date().getFullYear() + 10} value={editData.graduation_year || ''} onChange={(e) => handleInputChange('graduation_year', parseInt(e.target.value) || null)} className="w-full bg-accent rounded-t text-foreground px-4 py-3 border-b-2 border-transparent focus:border-emerald-600 focus:outline-none transition-colors duration-200 placeholder-gray-400" placeholder="e.g., 2024" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.graduation_year || '-'}</div>}
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2">Field of Study</label>
                                {isEditing ? <input type="text" value={editData.field_of_study || ''} onChange={(e) => handleInputChange('field_of_study', e.target.value)} className="w-full bg-accent rounded-t text-foreground px-4 py-3 border-b-2 border-transparent focus:border-emerald-600 focus:outline-none transition-colors duration-200 placeholder-gray-400" placeholder="e.g., Computer Science" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed">{profileData?.field_of_study || '-'}</div>}
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm text-muted-foreground mb-2">Academic Level</label>
                                {isEditing ? <CustomSelect value={editData.academic_level || ''} onChange={(value) => handleInputChange('academic_level', value)} options={academicLevelOptions} placeholder="Select academic level" /> : <div className="bg-accent rounded text-gray-400 px-4 py-3 cursor-not-allowed capitalize">{profileData?.academic_level?.replace('_', ' ') || '-'}</div>}
                            </div>
                        </div>
                    </div>
                );
            case 'social':
                return (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                            <div><h3 className="text-xl font-semibold text-foreground flex items-center space-x-2"><FiGlobe className="text-emerald-600 w-5 h-5" /><span>Social Links</span></h3></div>
                            {isEditable && (
                                <div className="flex items-center space-x-3">
                                    {isEditing ? (
                                        <>
                                            <button onClick={saveProfile} disabled={saving} className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 rounded-lg font-medium transition-colors duration-200">{saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}<span className='hidden md:flex'>{saving ? 'Saving...' : 'Save'}</span></button>
                                            <button onClick={cancelEdit} disabled={saving} className="flex items-center space-x-0.5 text-muted-foreground hover:text-foreground rounded-lg font-medium transition-colors duration-200"><FiX className="w-4 h-4" /><span className='hidden md:flex'>Cancel</span></button>
                                        </>
                                    ) : (
                                        <button onClick={() => setIsEditing(true)} className="flex items-center space-x-2 py-2 text-emerald-600 hover:text-emerald-700 rounded-lg font-medium transition-colors duration-200"><FiEdit3 className="w-4 h-4" /><span className='hidden md:flex'>Edit</span></button>
                                    )}
                                </div>
                            )}
                        </div>
                        {saveError && <div className="bg-red-100 text-red-700 border-l-4 border-red-500 px-4 py-2">{saveError}</div>}
                        <div className="grid grid-cols-1 gap-6 text-left">
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2 flex items-center space-x-2"><FiGithub className="w-4 h-4" /><span>GitHub Profile</span></label>
                                {isEditing ? <input type="url" value={editData.github_link || ''} onChange={(e) => handleInputChange('github_link', e.target.value)} className="w-full bg-accent rounded-t text-foreground px-4 py-3 border-b-2 border-transparent focus:border-emerald-600 focus:outline-none transition-colors duration-200 placeholder-gray-400" placeholder="https://github.com/yourusername" /> : <div className="text-muted-foreground px-4 py-3">{profileData?.github_link ? <a href={profileData.github_link} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline flex items-center space-x-2 transition-colors duration-200 overflow-x-hidden"><span className='truncate'>{profileData.github_link}</span><FiChevronRight className="w-4 h-4" /></a> : <span>-</span>}</div>}
                            </div>
                            <div>
                                <label className="block text-sm text-muted-foreground mb-2 flex items-center space-x-2"><FiLinkedin className="w-4 h-4" /><span>LinkedIn Profile</span></label>
                                {isEditing ? <input type="url" value={editData.linkedin_link || ''} onChange={(e) => handleInputChange('linkedin_link', e.target.value)} className="w-full bg-accent rounded-t text-foreground px-4 py-3 border-b-2 border-transparent focus:border-emerald-600 focus:outline-none transition-colors duration-200 placeholder-gray-400" placeholder="https://linkedin.com/in/yourusername" /> : <div className="text-muted-foreground px-4 py-3">{profileData?.linkedin_link ? <a href={profileData.linkedin_link} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline flex items-center space-x-2 transition-colors duration-200"><span className='truncate'>{profileData.linkedin_link}</span><FiChevronRight className="w-4 h-4" /></a> : <span>-</span>}</div>}
                            </div>
                        </div>
                    </div>
                );
            case 'achievements':
                return (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between border-b border-gray-200 pb-2 w-full">
                            <div><h3 className="text-xl font-semibold text-foreground flex items-center space-x-2"><FiAward className="text-emerald-600 w-5 h-5" /><span>Achievements</span></h3></div>
                            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                                <span className='hidden md:flex'>Earned: <span className="text-emerald-600 font-semibold ml-1">{badges.length}</span></span>
                                <span className='hidden md:flex'>Total: <span className="text-foreground font-semibold ml-1">{badgeProgress ? Object.values(badgeProgress.progress).flat().length : 0}</span></span>
                                <span className='lg:hidden md:hidden'><span className="text-emerald-600 font-semibold">{badges.length}</span></span>
                                <span className='lg:hidden md:hidden text-foreground font-semibold'>/</span>
                                <span className='lg:hidden md:hidden'><span className="text-foreground font-semibold"> {badgeProgress ? Object.values(badgeProgress.progress).flat().length : 0}</span></span>
                            </div>
                        </div>
                        {badgeProgress ? (
                            <div className="space-y-8">
                                {badgeProgress.progress.storyteller && badgeProgress.progress.storyteller.length > 0 && (
                                    <div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {badgeProgress.progress.storyteller.map((badge, index) => (
                                                <div key={`storyteller-${badge.badge_tier}`} className="relative cursor-pointer transition-all duration-300 group w-36">
                                                    <div className="flex flex-col items-center text-left w-full">
                                                        <div className="mb-2"><BadgeImage badgeId={badge.id} imagePath={badge.image_path} badgeTitle={badge.badge_title} badgeType="storyteller" tier={badge.badge_tier} isEarned={badge.earned} size="lg" earnedDescription={badge.earned_description} lockedDescription={badge.locked_description} /></div>
                                                        {badge.earned ? <div /> : (<div className="full"><div className="w-full bg-gray-700/50 rounded-full h-1"><div className="bg-emerald-600 h-1 rounded-full transition-all duration-300" style={{ width: `${badge.progressPercentage}%` }} /></div><p className="text-xs text-gray-500 mt-1">{badge.progressPercentage}% complete</p></div>)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {badgeProgress.progress.streak_master && badgeProgress.progress.streak_master.length > 0 && (
                                    <div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                            {badgeProgress.progress.streak_master.map((badge) => (
                                                <div key={`streak-${badge.badge_tier}`} className="relative cursor-pointer transition-all duration-300 group w-36" title={badge.badge_description}>
                                                    <div className="flex flex-col items-center text-left w-full">
                                                        <div className="mb-2"><BadgeImage badgeId={badge.id} imagePath={badge.image_path} badgeTitle={badge.badge_title} badgeType="streak_master" tier={badge.badge_tier} isEarned={badge.earned} size="lg" earnedDescription={badge.earned_description} lockedDescription={badge.locked_description} className="transition-transform duration-300" /></div>
                                                        <div className="space-y-1 mb-2"><h3 className={`text-xs font-bold leading-tight ${badge.earned ? 'text-foreground' : 'text-muted-foreground'}`}>{badge.badge_tier.charAt(0).toUpperCase() + badge.badge_tier.slice(1)}</h3></div>
                                                        {badge.earned ? <div /> : (<div className="w-1/2"><div className="w-full bg-gray-700/50 rounded-full h-1"><div className="bg-orange-500 h-1 rounded-full transition-all duration-300" style={{ width: `${badge.progressPercentage}%` }} /></div><p className="text-xs text-gray-500 mt-1 text-center">{badge.currentValue}/{badge.milestone_value}</p></div>)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {badgeProgress.progress.problem_crusher && badgeProgress.progress.problem_crusher.length > 0 && (
                                    <div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                            {badgeProgress.progress.problem_crusher.map((badge) => (
                                                <div key={`problem-${badge.badge_tier}`} className="relative cursor-pointer transition-all duration-300 group w-36" title={badge.badge_description}>
                                                    <div className="flex flex-col items-center text-center">
                                                        <div className="mb-3"><BadgeImage badgeId={badge.id} imagePath={badge.image_path} badgeTitle={badge.badge_title} badgeType="problem_crusher" tier={badge.badge_tier} isEarned={badge.earned} size="lg" earnedDescription={badge.earned_description} lockedDescription={badge.locked_description} className="transition-transform duration-300" /></div>
                                                        <div className="space-y-1 mb-2"><h3 className={`text-xs font-bold leading-tight ${badge.earned ? 'text-foreground' : 'text-muted-foreground'}`}>{badge.badge_tier.charAt(0).toUpperCase() + badge.badge_tier.slice(1)}</h3></div>
                                                        {badge.earned ? <div /> : (<div className="w-1/2"><div className="w-full bg-gray-700/50 rounded-full h-1"><div className="bg-blue-500 h-1 rounded-full transition-all duration-300" style={{ width: `${badge.progressPercentage}%` }} /></div><p className="text-xs text-gray-500 mt-1">{badge.currentValue}/{badge.milestone_value}</p></div>)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {badgeProgress.progress.daily_champion && badgeProgress.progress.daily_champion.length > 0 && (
                                    <div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                            {badgeProgress.progress.daily_champion.map((badge) => (
                                                <div key={`daily-${badge.badge_tier}`} className="relative cursor-pointer transition-all duration-300 group w-36" title={badge.badge_description}>
                                                    <div className="flex flex-col items-center text-center">
                                                        <div className="mb-3"><BadgeImage badgeId={badge.id} imagePath={badge.image_path} badgeTitle={badge.badge_title} badgeType="daily_champion" tier={badge.badge_tier} isEarned={badge.earned} size="lg" earnedDescription={badge.earned_description} lockedDescription={badge.locked_description} className="transition-transform duration-300" /></div>
                                                        <div className="space-y-1 mb-2"><h3 className={`text-xs font-bold leading-tight ${badge.earned ? 'text-foreground' : 'text-muted-foreground'}`}>{badge.badge_tier.charAt(0).toUpperCase() + badge.badge_tier.slice(1)}</h3></div>
                                                        {badge.earned ? <div /> : (<div className="w-1/2"><div className="w-full bg-gray-700/50 rounded-full h-1"><div className="bg-yellow-500 h-1 rounded-full transition-all duration-300" style={{ width: `${badge.progressPercentage}%` }} /></div><p className="text-xs text-gray-500 mt-1">{badge.currentValue}/{badge.milestone_value}</p></div>)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12"><FiRefreshCw className="text-4xl text-gray-500 mx-auto mb-4 animate-spin" /><p className="text-muted-foreground">Loading badges...</p></div>
                        )}
                    </div>
                );
            default:
                return <div>Select a category</div>;
        }
    };

    useEffect(() => {
        fetchProfileData();

        // Handle deep-linking to specific tabs
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab && categories.some(cat => cat.id === tab)) {
            setSelectedCategory(tab);
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        const username = profileData?.username || user?.username;
        document.title = username ? `SimpleCode | ${username}` : 'SimpleCode | Profile';
        return () => { document.title = 'SimpleCode'; };
    }, [profileData, user]);

    if (loading) {
        return <ProfilePageSkeleton navigate={navigate} navLinks={navLinks} />;
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex items-center justify-center font-sans">
                <div className="text-center bg-white p-8 rounded border-l-4 border-red-500 shadow-sm max-w-md mx-auto">
                    <FiAlertCircle className="text-6xl text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-foreground mb-2">Error Loading Profile</h2>
                    <p className="text-muted-foreground mb-6">{error}</p>
                    <button onClick={fetchProfileData} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded transition-all duration-200 flex items-center space-x-2 mx-auto"><FiRefreshCw className="text-lg" /><span>Retry</span></button>
                </div>
            </div>
        );
    }

    // (navLinks and footerLinks moved to top of component)

    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground overflow-x-hidden scroll-smooth font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            {/* Main Content */}
            <main className="max-w-8xl mx-auto pt-24 px-4 sm:px-12 lg:px-16 pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    <div className="lg:col-span-1">
                        {/* Mobile Category Dropdown */}
                        <div className="lg:hidden mb-4">
                            <button onClick={toggleMobileCategories} className="w-full flex items-center justify-between px-4 py-3 bg-white border border-border rounded shadow-sm text-foreground">
                                <div className="flex items-center space-x-2">
                                    {React.createElement(categories.find(cat => cat.id === selectedCategory)?.icon || FiUser, { className: "text-emerald-600 w-5 h-5" })}
                                    <span className="font-semibold">{categories.find(cat => cat.id === selectedCategory)?.name}</span>
                                </div>
                                <div className={`transform transition-transform duration-200 ${isMobileCategoriesOpen ? 'rotate-180' : ''}`}><FiChevronDown className="w-4 h-4 text-muted-foreground" /></div>
                            </button>
                            <div className={`grid transition-all duration-300 ease-in-out ${isMobileCategoriesOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 mt-0'}`}>
                                <div className="overflow-hidden bg-white border border-border rounded shadow-lg">
                                    {categories.map((category) => (
                                        <button 
                                            key={category.id} 
                                            onClick={() => handleCategorySelect(category.id)} 
                                            className={`w-full text-left px-4 py-3 transition-all duration-200 flex items-center space-x-3 ${
                                                selectedCategory === category.id 
                                                ? 'bg-emerald-50 text-emerald-700 border-l-4 border-l-emerald-600' 
                                                : 'text-foreground hover:bg-emerald-50 hover:text-emerald-700 border-l-4 border-l-transparent'
                                            }`}
                                        >
                                            <category.icon className="w-5 h-5 flex-shrink-0" />
                                            <div>
                                                <div className="font-medium">{category.name}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Desktop Sidebar */}
                        <div className="hidden lg:block space-y-4">
                            {/* Upper Profile Container */}
                            <div className="bg-white rounded overflow-hidden px-6 py-3 sticky top-24 border-l-4 border-emerald-600">
                                <div className="flex items-center space-x-4">
                                    {user?.avatar_url ? (
                                        <img src={user.avatar_url} alt="Profile" className="w-16 h-16 rounded-full object-cover flex-shrink-0 border-2 border-emerald-600" referrerPolicy="no-referrer" />
                                    ) : (
                                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center border-2 border-emerald-600"><span className="text-2xl font-bold text-emerald-700 capitalize">{user?.name?.charAt(0) || user?.username?.charAt(0) || 'U'}</span></div>
                                    )}
                                    <div>
                                        <h2 className="text-xl font-bold text-foreground text-left capitalize truncate">{user?.name || user?.username}</h2>
                                        <p className="text-sm text-muted-foreground text-left font-medium">@{user?.username}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Lower Table-like Container */}
                            <div className="bg-white rounded border border-gray-200 overflow-hidden shadow-sm sticky top-[280px]">
                                <h3 className="px-6 py-3 text-sm font-bold text-white bg-emerald-600 uppercase tracking-widest text-center">Profile Sections</h3>
                                <div>
                                    {categories.map((category) => (<button key={category.id} onClick={() => handleCategorySelect(category.id)} className={`w-full text-left px-6 py-3 transition-all duration-200 flex items-start space-x-3 ${selectedCategory === category.id ? 'bg-gray-100 text-emerald-700' : 'text-foreground hover:bg-gray-100'}`}>
                                        <category.icon className={`w-5 h-5 flex-shrink-0 mt-1 ${selectedCategory === category.id ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                                        <div><div className={`font-semibold ${selectedCategory === category.id ? 'text-emerald-700' : 'text-foreground'}`}>{category.name}</div><div className="text-xs text-muted-foreground mt-0.5">{category.description}</div></div>
                                    </button>))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-3 space-y-6">
                        {completionPercentage < 100 && (<div><ProfileCompletionProgress completionPercentage={completionPercentage} /></div>)}
                        <div className="bg-white border-l-4 border-emerald-600 rounded shadow-sm p-6 pt-3 pb-6">{renderCategoryContent()}</div>
                    </div>
                </div>
            </main>

            {showBadgePopup && (
                <>
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={closeBadgePopup} />
                    <div className="fixed inset-0 flex items-center justify-center z-50">
                        <div className="relative bg-white border-2 border-emerald-600 rounded shadow-xl px-8 py-6 max-w-md mx-4 text-center">
                            <button onClick={closeBadgePopup} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition"><FiX className="w-5 h-5" /></button>
                            <h3 className="text-2xl font-bold text-foreground mb-2">Congratulations!</h3>
                            <div className="space-y-4 flex items-center justify-center flex-col">
                                {earnedBadges.map((badge, index) => (
                                    <div key={badge.id} className='flex flex-col items-center space-y-2'>
                                        <div className="flex items-center space-x-4 text-center">
                                            <BadgeImage badgeId={badge.badge_types?.id} imagePath={badge.badge_types?.image_path} badgeTitle={badge.badge_types?.badge_title} badgeType={badge.badge_types?.badge_name} tier={badge.badge_types?.badge_tier} isEarned={true} size="xl" />
                                        </div>
                                        <p className="text-muted-foreground font-medium mb-6">You earned a new badge!</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * SKELETON LOADER
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component ProfilePageSkeleton
 * @description Skeleton loader for the Profile Page to reduce layout shift during initial data fetch.
 * 
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @param {Array} props.navLinks - Pre-filtered navigation links for the header.
 */
const ProfilePageSkeleton = ({ navigate, navLinks }) => {
    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground font-sans flex flex-col">
            <Navbar navigate={navigate} navLinks={navLinks} />
            <main className="flex-1 max-w-8xl mx-auto w-full pt-24 px-4 sm:px-12 lg:px-16 pb-20 animate-pulse">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    <div className="lg:col-span-1">
                        <div className="hidden lg:block space-y-6">
                            <div className="bg-white rounded border-l-4 border-emerald-600 overflow-hidden shadow-sm relative">
                                <div className="p-6">
                                    <div className="flex items-center space-x-6">
                                        <div className="w-20 h-20 bg-gray-200 rounded-full"></div>
                                        <div className="flex-1 space-y-3">
                                            <div className="h-5 w-32 bg-gray-200 rounded"></div>
                                            <div className="h-4 w-24 bg-gray-200 rounded"></div>
                                        </div>
                                    </div>
                                    <div className="mt-8 mb-3 h-2 w-full bg-gray-200 rounded"></div>
                                    <div className="h-3 w-48 bg-gray-200 rounded"></div>
                                </div>
                            </div>
                            <div className="bg-white rounded border border-gray-200 overflow-hidden shadow-sm">
                                <h3 className="px-6 py-3 text-sm font-bold text-white bg-emerald-600 uppercase tracking-widest text-center animate-none"></h3>
                                <div>
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="h-16 w-full bg-white border-b border-gray-50 flex items-center px-6 space-x-3">
                                             <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
                                             <div className="space-y-2">
                                                 <div className="h-3 w-24 bg-gray-200 rounded"></div>
                                                 <div className="h-2 w-32 bg-gray-200 rounded"></div>
                                             </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="lg:hidden h-14 bg-white border border-gray-200 rounded shadow-sm"></div>
                    </div>
                    <div className="lg:col-span-3 space-y-6">
                        <div className="h-24 bg-white rounded shadow-sm p-6">
                            <div className="h-6 w-1/3 bg-gray-200 rounded mb-3"></div>
                            <div className="h-2 w-full bg-gray-200 rounded"></div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded shadow-sm relative overflow-hidden">
                            <div className="flex items-center justify-between border-b border-gray-200 p-6">
                                <div className="h-6 w-1/3 bg-gray-200 rounded"></div>
                                <div className="h-8 w-16 bg-gray-200 rounded-lg"></div>
                            </div>
                            <div className="p-6 sm:p-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <div key={i} className="space-y-2">
                                            <div className="h-4 w-1/4 bg-gray-200 rounded"></div>
                                            <div className="h-12 w-full bg-gray-100 rounded-t border-b-2 border-transparent"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ProfilePage;